import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import './InvoiceManagement.css';
import { API_BASE_URL, getAuthHeaders } from '../../config/api.config';
import { RechnungStornoBereich, NachmeldungDialog } from './RechnungskorrekturDialoge'; // T-165 (v1.8.46)
import { ZahlungErfassenBereich } from './ZahlungErfassenBereich'; // E8/T-064 (v1.8.49)

interface Invoice {
  id: number;
  invoiceNumber: string;
  type: string;
  typeDisplayName: string;
  invoiceDate: string;
  dueDate?: string;
  totalAmount: number;
  amountDue: number;
  amountPaid: number;
  status: string;
  billingName?: string;  // camelCase from API
  BillingName?: string;  // PascalCase from API - fallback
  bookingId: number;
  bookingNumber?: string;  // Bestellnummer aus Booking
  // Referenzfelder für Migration
  customerReference?: string;  // Alte Rechnungsnummer / Kundenreferenz
  adminNotes?: string;         // Interne Notizen (nur Admin sichtbar)
}

interface Booking {
  id: number;
  bookingNumber: string;
  customerName: string;
  companyName?: string;
  bookingDate: string;
  // T-085 (v1.8.19): Fahrttermin und Abfahrtszeit aus dem Schedule (bookingDate ist das Anlagedatum)
  scheduleDate?: string | null;
  startTime?: string | null;
  participantCount: number;
  variant: string;
  status: string;
  totalAmount: number;
  // Voucher discount (if any) - supports both camelCase and PascalCase from API
  voucherDiscount?: number;
  VoucherDiscount?: number;
  // Final amount after voucher discount - supports both camelCase and PascalCase from API
  finalAmount?: number;
  FinalAmount?: number;
  createdAt: string;
  paymentStatus: string;
  isDeleted: boolean;
  // Invoice references - null if not yet created
  depositInvoiceId?: number | null;
  finalInvoiceId?: number | null;
}

// TODO: TourType interface für späteren Tour-Konfigurations-Filter
// interface TourType {
//   id: number;
//   code: string;
//   name: string;
//   isActive: boolean;
// }

const InvoiceManagement: React.FC = () => {
  const location = useLocation();
  const initialSearchTerm = (location.state as { searchBookingNumber?: string })?.searchBookingNumber || '';

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'list' | 'generate' | 'detail'>('list');
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [bookingSearchTerm, setBookingSearchTerm] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<string>('all');
  // TODO: Tour-Konfigurations-Filter später implementieren wenn Buchungen TourTypeId haben
  // const [tourTypeFilter, setTourTypeFilter] = useState<string>('all');
  // Referenzfelder Bearbeitung
  const [editCustomerRef, setEditCustomerRef] = useState('');
  const [editAdminNotes, setEditAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);
  // Track which booking is currently being processed (for button states)
  const [generatingBookingId, setGeneratingBookingId] = useState<number | null>(null);
  const [generatingType, setGeneratingType] = useState<string | null>(null);
  // Track which invoice is currently sending email
  const [sendingEmailInvoiceId, setSendingEmailInvoiceId] = useState<number | null>(null);
  // Success message for email sent
  const [emailSuccessMessage, setEmailSuccessMessage] = useState<string | null>(null);
  // T-165 (v1.8.46): Dialog "Nachmeldung abrechnen" für eine Buchung ohne Schlussrechnung
  const [nachmeldungBuchung, setNachmeldungBuchung] = useState<{ id: number; bookingNumber: string } | null>(null);

  const API_BASE = API_BASE_URL;

  // Hilfsfunktion für sichere Datumsformatierung
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      // Prüfe ob das Datum gültig ist und nicht Jahr 1 oder ähnlich
      if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
        return '-';
      }
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  // Hilfsfunktion für Währungsformatierung
  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return '0,00 €';
    return amount.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) + ' €';
  };

  useEffect(() => {
    loadInvoices();
    loadBookings();
    // TODO: loadTourTypes() später für Tour-Konfigurations-Filter
  }, []);

  // Update search term when navigating from BookingDetailsDialog
  useEffect(() => {
    const bookingNumber = (location.state as { searchBookingNumber?: string })?.searchBookingNumber;
    if (bookingNumber) {
      setSearchTerm(bookingNumber);
    }
  }, [location.state]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.invoices || []);
      setError(null);
    } catch (err: any) {
      setError(`Fehler beim Laden der Rechnungen: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadBookings = async () => {
    try {
      const token = localStorage.getItem('authToken');
      // Request all bookings with extended date range for invoice creation
      // T-085 (v1.8.19): Fahrttermine bis 24 Monate in der Zukunft (Altfall-Uebernahmen fuer die naechste Saison)
      const dateFrom = new Date();
      dateFrom.setMonth(dateFrom.getMonth() - 12);
      const dateTo = new Date();
      dateTo.setMonth(dateTo.getMonth() + 24);
      const response = await axios.get(
        `${API_BASE}/bookings?pageSize=500&dateFrom=${dateFrom.toISOString()}&dateTo=${dateTo.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // API returns paginated response: { items: [...], pagination: {...} }
      const bookingsArray = response.data.items || response.data || [];
      // Filter for bookings eligible for invoice creation:
      // - Status NOT IN ('FullyPaid', 'PendingApproval')
      // - PaymentStatus != 'Completed'
      // - Not deleted
      const eligibleBookings = bookingsArray.filter((b: Booking) =>
        b.status !== 'FullyPaid' &&
        b.status !== 'PendingApproval' &&
        b.paymentStatus !== 'Completed' &&
        !b.isDeleted &&
        b.totalAmount > 0
      );
      setBookings(eligibleBookings);
    } catch (err: any) {
      console.error('Error loading bookings:', err);
    }
  };

  // TODO: loadTourTypes für späteren Tour-Konfigurations-Filter
  // const loadTourTypes = async () => {
  //   try {
  //     const token = localStorage.getItem('authToken');
  //     const response = await axios.get(
  //       `${API_BASE}/tour-types`,
  //       { headers: { Authorization: `Bearer ${token}` } }
  //     );
  //     setTourTypes(response.data || []);
  //   } catch (err: any) {
  //     console.error('Error loading tour types:', err);
  //   }
  // };

  const generateInvoice = async (bookingId: number, type: 'anzahlung' | 'schlussrechnung' | 'kurzfristig' | 'nachtrag') => {
    const typeLabels: Record<string, string> = {
      'anzahlung': 'Anzahlungsrechnung',
      'schlussrechnung': 'Schlussrechnung',
      'kurzfristig': 'Kurzfristige Rechnung',
      'nachtrag': 'Nachtragsrechnung' // T-065 (v1.8.26)
    };
    const typeLabel = typeLabels[type] || 'Rechnung';

    try {
      setGeneratingBookingId(bookingId);
      setGeneratingType(type);
      setError(null);

      const token = localStorage.getItem('authToken');

      // T-065 (v1.8.26): Nachtragsrechnung nur nach Vorschau und Bestätigung (Differenz seit der Schlussrechnung)
      if (type === 'nachtrag') {
        const preview = await axios.get(
          `${API_BASE}/invoices/booking/${bookingId}/nachtrag-vorschau`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const v = preview.data || {};
        const moeglich = v.moeglich ?? v.Moeglich;
        const hinweis = v.hinweis ?? v.Hinweis ?? '';
        if (!moeglich) {
          setError(`Nachtragsrechnung nicht möglich:\n\n${hinweis}`);
          return;
        }
        const positionen = (v.positionen ?? v.Positionen ?? []) as any[];
        const fmt = (n: number) => Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
        const lines = positionen.map(p => `  ${p.menge ?? p.Menge} x ${p.beschreibung ?? p.Beschreibung}: ${fmt(p.gesamt ?? p.Gesamt)}`).join('\n');
        const ok = window.confirm(
          `Nachtragsrechnung zu ${v.schlussrechnungNummer ?? v.SchlussrechnungNummer} erstellen?\n\n` +
          `${lines}\n\nNachtrag (Zahlbetrag): ${fmt(v.nachtrag ?? v.Nachtrag)}\n` +
          `Gesamtbetrag der Buchung neu: ${fmt(v.bruttoAktuell ?? v.BruttoAktuell)}, bereits berechnet: ${fmt(v.bereitsBerechnet ?? v.BereitsBerechnet)}\n\n` +
          `Die Rechnung wird erstellt, aber noch nicht versendet (Versand über die Rechnungsliste).`
        );
        if (!ok) return;
      }

      const response = await axios.post(
        `${API_BASE}/invoices/booking/${bookingId}/${type}?sendEmail=false`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`${typeLabel} erfolgreich erstellt!\n\nRechnungsnummer: ${response.data.invoiceNumber || 'N/A'}`);
        loadInvoices();
        loadBookings(); // Refresh bookings to update available actions
        setView('list');
      } else {
        // Handle case where success is false but no error thrown
        setError(`${typeLabel} konnte nicht erstellt werden: ${response.data.errorMessage || 'Unbekannter Fehler'}`);
      }
    } catch (err: any) {
      // Extract error message from various possible response formats
      let errorMessage = 'Unbekannter Fehler';
      if (err.response?.data) {
        // Backend may return error as string directly or in errorMessage/message property
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.errorMessage) {
          errorMessage = err.response.data.errorMessage;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(`${typeLabel} konnte nicht erstellt werden:\n\n${errorMessage}`);
    } finally {
      setGeneratingBookingId(null);
      setGeneratingType(null);
    }
  };

  const downloadPdf = async (invoiceId: number, invoiceNumber: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError(`Fehler beim Download: ${err.message}`);
    }
  };

  // T-171 (v1.8.47): PDF der Stornorechnung zu einer stornierten Rechnung (Rechnungsarchiv-Endpunkt)
  const downloadStornorechnung = async (invoiceId: number, invoiceNumber: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/invoice-archive/stornorechnung/Buchung/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const kopf = String(response.headers['content-disposition'] || '');
      const name = /filename="?([^";]+)"?/i.exec(kopf)?.[1] || `Storno-${invoiceNumber}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.response?.status === 404
        ? `Zu ${invoiceNumber} gibt es noch keine Stornorechnung. Bitte im Rechnungsarchiv "Stornorechnungen erzeugen" wählen.`
        : `Fehler beim Download der Stornorechnung: ${err.message}`);
    }
  };

  const sendInvoiceEmail = async (invoiceId: number, invoiceNumber?: string) => {
    // Prevent double-clicks
    if (sendingEmailInvoiceId !== null) return;

    try {
      setSendingEmailInvoiceId(invoiceId);
      setEmailSuccessMessage(null);
      setError(null);

      const token = localStorage.getItem('authToken');
      await axios.post(
        `${API_BASE}/invoices/${invoiceId}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Show success message
      const displayNumber = invoiceNumber || `ID ${invoiceId}`;
      setEmailSuccessMessage(`Rechnung ${displayNumber} erfolgreich per E-Mail versendet!`);

      // Reload invoices to show updated status (Sent)
      await loadInvoices();

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setEmailSuccessMessage(null);
      }, 5000);

    } catch (err: any) {
      // Extract error message from response
      let errorMessage = 'Unbekannter Fehler';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(`Fehler beim E-Mail-Versand: ${errorMessage}`);
    } finally {
      setSendingEmailInvoiceId(null);
    }
  };

  // Detailansicht öffnen
  const openInvoiceDetail = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setEditCustomerRef(invoice.customerReference || '');
    setEditAdminNotes(invoice.adminNotes || '');
    setView('detail');
  };

  // Referenzfelder speichern
  const saveInvoiceReferences = async () => {
    if (!selectedInvoice) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('authToken');
      await axios.patch(
        `${API_BASE}/invoices/${selectedInvoice.id}`,
        {
          customerReference: editCustomerRef || null,
          adminNotes: editAdminNotes || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Lokalen State aktualisieren
      setInvoices(prev => prev.map(inv =>
        inv.id === selectedInvoice.id
          ? { ...inv, customerReference: editCustomerRef, adminNotes: editAdminNotes }
          : inv
      ));
      setSelectedInvoice(prev => prev ? { ...prev, customerReference: editCustomerRef, adminNotes: editAdminNotes } : null);

      alert('Referenzfelder erfolgreich gespeichert!');
    } catch (err: any) {
      setError(`Fehler beim Speichern: ${err.response?.data?.message || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, string> = {
      'Draft': 'status-draft',
      'Created': 'status-created',
      'Sent': 'status-sent',
      'Viewed': 'status-viewed',
      'Paid': 'status-paid',
      'PartiallyPaid': 'status-partial',
      'Overdue': 'status-overdue',
      'Cancelled': 'status-cancelled',
      'Disputed': 'status-disputed'
    };
    return statusMap[status] || 'status-default';
  };

  const getStatusLabel = (status: string) => {
    const statusLabels: Record<string, string> = {
      'Draft': 'Entwurf',
      'Created': 'Erstellt',
      'Sent': 'Versendet',
      'Viewed': 'Angesehen',
      'Paid': 'Bezahlt',
      'PartiallyPaid': 'Teilweise bezahlt',
      'Overdue': 'Überfällig',
      'Cancelled': 'Storniert',
      'Disputed': 'Streitfall'
    };
    return statusLabels[status] || status;
  };

  // Typ-Badge Klasse
  const getTypeBadge = (type: string) => {
    const typeMap: Record<string, string> = {
      'Anzahlung': 'type-anzahlung',
      'Schlussrechnung': 'type-schlussrechnung',
      'Kurzfristige': 'type-kurzfristig',
      'Kurzfristig': 'type-kurzfristig',
      'Nachbuchung': 'type-nachtrag', // T-065 (v1.8.26): Nachtragsrechnung
      'Storno': 'type-storno',
      'Gutschrift': 'type-gutschrift'
    };
    return typeMap[type] || 'type-default';
  };

  // Typ-Label Kurzform
  const getTypeShort = (type: string) => {
    const typeShort: Record<string, string> = {
      'Anzahlung': 'Anzahlung',
      'Schlussrechnung': 'Schluss',
      'Kurzfristige': 'Kurzfr.',
      'Kurzfristig': 'Kurzfr.',
      'Nachbuchung': 'Nachtrag', // T-065 (v1.8.26)
      'Storno': 'Storno',
      'Gutschrift': 'Gutschrift'
    };
    return typeShort[type] || type;
  };

  // T-065 (v1.8.26): Langform des Rechnungstyps für die Detailansicht (API liefert den Enum-Namen)
  const getTypeLong = (type: string) => {
    const typeLong: Record<string, string> = {
      'Anzahlung': 'Anzahlungsrechnung',
      'Schlussrechnung': 'Schlussrechnung',
      'Kurzfristig': 'Schlussrechnung (Komplettzahlung)',
      'Nachbuchung': 'Nachtragsrechnung'
    };
    return typeLong[type] || type;
  };

  // Filter and search logic for invoices
  const filteredInvoices = invoices.filter(invoice => {
    const customerName = invoice.billingName || invoice.BillingName || '';
    const bookingNum = invoice.bookingNumber || '';
    const matchesSearch =
      invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.typeDisplayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookingNum.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Filter and search logic for bookings
  const filteredBookings = bookings.filter(booking => {
    const matchesSearch =
      booking.bookingNumber.toLowerCase().includes(bookingSearchTerm.toLowerCase()) ||
      booking.customerName.toLowerCase().includes(bookingSearchTerm.toLowerCase()) ||
      (booking.companyName && booking.companyName.toLowerCase().includes(bookingSearchTerm.toLowerCase()));

    // Invoice status filter
    const hasDeposit = booking.depositInvoiceId != null;
    const hasFinal = booking.finalInvoiceId != null;
    const hasAnyInvoice = hasDeposit || hasFinal;
    const isFullyInvoiced = hasDeposit && hasFinal;
    const hasOnlyDeposit = hasDeposit && !hasFinal;

    let matchesInvoiceStatus = true;
    if (invoiceStatusFilter === 'none') {
      matchesInvoiceStatus = !hasAnyInvoice;
    } else if (invoiceStatusFilter === 'partial') {
      matchesInvoiceStatus = hasOnlyDeposit;
    } else if (invoiceStatusFilter === 'complete') {
      matchesInvoiceStatus = isFullyInvoiced;
    } else if (invoiceStatusFilter === 'pending') {
      matchesInvoiceStatus = !isFullyInvoiced; // Needs at least one more invoice
    }

    return matchesSearch && matchesInvoiceStatus;
  });

  if (loading && invoices.length === 0) {
    return <div className="loading">Lädt Rechnungen...</div>;
  }

  return (
    <div className="invoice-management">
      <div className="invoice-header">
        <h1>Rechnungsverwaltung</h1>
        <div className="header-actions">
          <button
            className={view === 'list' ? 'btn-primary active' : 'btn-secondary'}
            onClick={() => setView('list')}
          >
            Rechnungen ({invoices.length})
          </button>
          <button
            className={view === 'generate' ? 'btn-primary active' : 'btn-secondary'}
            onClick={() => setView('generate')}
          >
            Neue Rechnung erstellen
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {emailSuccessMessage && (
        <div className="success-message">
          {emailSuccessMessage}
          <button onClick={() => setEmailSuccessMessage(null)}>✕</button>
        </div>
      )}

      {view === 'list' ? (
        <div className="invoices-list">
          <div className="search-filter-bar">
            <input
              type="text"
              className="search-input"
              placeholder="Suche nach Rechnungsnr., Buchungsnr., Kunde oder Typ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Alle Status</option>
              <option value="Draft">Entwurf</option>
              <option value="Created">Erstellt</option>
              <option value="Sent">Versendet</option>
              <option value="Viewed">Angesehen</option>
              <option value="Paid">Bezahlt</option>
              <option value="PartiallyPaid">Teilweise bezahlt</option>
              <option value="Overdue">Überfällig</option>
              <option value="Cancelled">Storniert</option>
              <option value="Disputed">Streitfall</option>
            </select>
            <div className="results-count">
              {filteredInvoices.length} von {invoices.length} Rechnungen
            </div>
          </div>

          <table className="invoices-table">
            <thead>
              <tr>
                <th className="col-number">Rechnungsnr.</th>
                <th className="col-booking">Buchungsnr.</th>
                <th className="col-type">Typ</th>
                <th className="col-customer">Kunde</th>
                <th className="col-date">Erstellt</th>
                <th className="col-date">Fällig</th>
                <th className="col-amount">Betrag</th>
                <th className="col-amount">Offen</th>
                <th className="col-status">Status</th>
                <th className="col-actions">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map(invoice => (
                <tr key={invoice.id} onClick={() => {
                  // Set booking filter and switch to generate view
                  if (invoice.bookingNumber) {
                    setBookingSearchTerm(invoice.bookingNumber);
                  }
                  setView('generate');
                  // Also show invoice details below
                  setSelectedInvoice(invoice);
                  setEditCustomerRef(invoice.customerReference || '');
                  setEditAdminNotes(invoice.adminNotes || '');
                }}>
                  <td className="invoice-number">{invoice.invoiceNumber}</td>
                  <td className="booking-number">{invoice.bookingNumber || '-'}</td>
                  <td>
                    <span className={`type-badge ${getTypeBadge(invoice.typeDisplayName)}`}>
                      {getTypeShort(invoice.typeDisplayName)}
                    </span>
                  </td>
                  <td className="customer-name">{invoice.billingName || invoice.BillingName || <span className="text-muted">-</span>}</td>
                  <td className="date">{formatDate(invoice.invoiceDate)}</td>
                  <td className="date">{formatDate(invoice.dueDate)}</td>
                  <td className="amount">{formatCurrency(invoice.totalAmount)}</td>
                  <td className={`amount ${invoice.amountDue > 0 ? 'amount-open' : 'amount-paid'}`}>
                    {formatCurrency(invoice.amountDue)}
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadge(invoice.status)}`}>
                      {getStatusLabel(invoice.status)}
                    </span>
                  </td>
                  <td className="actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn-action btn-pdf"
                      onClick={() => downloadPdf(invoice.id, invoice.invoiceNumber)}
                      title="PDF herunterladen"
                    >
                      📄 PDF
                    </button>
                    <button
                      className={`btn-action btn-email ${sendingEmailInvoiceId === invoice.id ? 'btn-loading' : ''}`}
                      onClick={() => sendInvoiceEmail(invoice.id, invoice.invoiceNumber)}
                      disabled={sendingEmailInvoiceId !== null}
                      title={sendingEmailInvoiceId === invoice.id ? 'E-Mail wird gesendet...' : 'E-Mail senden'}
                    >
                      {sendingEmailInvoiceId === invoice.id ? '⏳' : '✉️'} E-Mail
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {invoices.length === 0 && (
            <div className="empty-state">
              <p>Keine Rechnungen vorhanden</p>
              <button onClick={() => setView('generate')}>Erste Rechnung erstellen</button>
            </div>
          )}
        </div>
      ) : (
        <div className="generate-invoice">
          <h2>Neue Rechnung erstellen</h2>

          <div className="search-filter-bar">
            <input
              type="text"
              className="search-input"
              placeholder="Suche nach Buchungsnr., Kunde oder Firma..."
              value={bookingSearchTerm}
              onChange={(e) => setBookingSearchTerm(e.target.value)}
            />
            {/* TODO: Tour-Konfigurations-Filter später implementieren wenn Buchungen TourTypeId haben */}
            <select
              className="status-filter"
              value={invoiceStatusFilter}
              onChange={(e) => setInvoiceStatusFilter(e.target.value)}
            >
              <option value="all">Alle Rechnungsstatus</option>
              <option value="none">Ohne Rechnung</option>
              <option value="pending">Offen (braucht Rechnung)</option>
              <option value="partial">Nur Anzahlung</option>
              <option value="complete">Vollständig fakturiert</option>
            </select>
            <div className="results-count">
              {filteredBookings.length} von {bookings.length} Buchungen
            </div>
          </div>

          <div className="bookings-grid">
            {filteredBookings.map(booking => {
              const hasDeposit = booking.depositInvoiceId != null;
              const hasFinal = booking.finalInvoiceId != null;
              const hasFullInvoice = hasDeposit && hasFinal && booking.depositInvoiceId === booking.finalInvoiceId;

              return (
                <div key={booking.id} className="booking-card">
                  <div className="booking-header">
                    <h3>{booking.customerName}</h3>
                    <span className="booking-id">#{booking.bookingNumber}</span>
                  </div>
                  {/* Invoice status badges */}
                  {(hasDeposit || hasFinal) && (
                    <div className="invoice-status-badges">
                      {hasFullInvoice ? (
                        <span className="invoice-badge badge-kurzfristig">✓ Kurzfristige Rechnung erstellt</span>
                      ) : (
                        <>
                          {hasDeposit && <span className="invoice-badge badge-anzahlung">✓ Anzahlung erstellt</span>}
                          {hasFinal && <span className="invoice-badge badge-schluss">✓ Schlussrechnung erstellt</span>}
                        </>
                      )}
                    </div>
                  )}
                  <div className="booking-details">
                    {/* T-085 (v1.8.19): Fahrttermin statt Anlagedatum; Rueckfall auf bookingDate nur bei altem Backend */}
                    <p><strong>Termin:</strong> {new Date(booking.scheduleDate || booking.bookingDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}{booking.startTime ? `, ${booking.startTime} Uhr` : ''}</p>
                    <p><strong>Teilnehmer:</strong> {booking.participantCount}</p>
                    <p><strong>Variante:</strong> {booking.variant}</p>
                    {(() => {
                      const voucherDiscount = booking.voucherDiscount || booking.VoucherDiscount || 0;
                      const finalAmount = booking.finalAmount || booking.FinalAmount || booking.totalAmount;
                      return (
                        <>
                          {voucherDiscount > 0 && (
                            <p style={{ color: '#16a34a' }}>
                              <strong>Gutschein:</strong> -{voucherDiscount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                            </p>
                          )}
                          <p><strong>Gesamtpreis:</strong> {finalAmount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
                        </>
                      );
                    })()}
                  </div>
                  <div className="booking-actions">
                    <button
                      className={`btn-primary btn-sm ${hasDeposit ? 'btn-disabled-done' : ''}`}
                      onClick={() => generateInvoice(booking.id, 'anzahlung')}
                      disabled={generatingBookingId !== null || hasDeposit}
                      title={hasDeposit ? 'Anzahlungsrechnung bereits erstellt' : 'Anzahlungsrechnung erstellen'}
                    >
                      {generatingBookingId === booking.id && generatingType === 'anzahlung'
                        ? '⏳ Wird erstellt...'
                        : hasDeposit ? '✓ Anzahlung erstellt' : 'Anzahlungsrechnung (20%)'}
                    </button>
                    <button
                      className={`btn-primary btn-sm ${hasFinal ? 'btn-disabled-done' : ''}`}
                      onClick={() => generateInvoice(booking.id, 'schlussrechnung')}
                      disabled={generatingBookingId !== null || hasFinal}
                      title={hasFinal ? 'Schlussrechnung bereits erstellt' : 'Schlussrechnung erstellen'}
                    >
                      {generatingBookingId === booking.id && generatingType === 'schlussrechnung'
                        ? '⏳ Wird erstellt...'
                        : hasFinal ? '✓ Schlussrechnung erstellt' : 'Schlussrechnung (80%)'}
                    </button>
                    <button
                      className={`btn-primary btn-sm ${hasFullInvoice ? 'btn-disabled-done' : ''}`}
                      onClick={() => generateInvoice(booking.id, 'kurzfristig')}
                      disabled={generatingBookingId !== null || hasDeposit || hasFinal}
                      title={hasFullInvoice ? 'Kurzfristige Rechnung bereits erstellt' : (hasDeposit || hasFinal) ? 'Bereits andere Rechnung erstellt' : 'Kurzfristige Rechnung erstellen'}
                    >
                      {generatingBookingId === booking.id && generatingType === 'kurzfristig'
                        ? '⏳ Wird erstellt...'
                        : hasFullInvoice ? '✓ Kurzfristig erstellt' : 'Kurzfristige Rechnung (100%)'}
                    </button>
                    {/* T-065 (v1.8.26): Nachtragsrechnung nur nach gestellter Schlussrechnung bzw. Komplettrechnung */}
                    {hasFinal && (
                      <button
                        className="btn-primary btn-sm"
                        onClick={() => generateInvoice(booking.id, 'nachtrag')}
                        disabled={generatingBookingId !== null}
                        title="Nachtragsrechnung für Nachbuchungen seit der Schlussrechnung (Vorschau, dann Bestätigung)"
                      >
                        {generatingBookingId === booking.id && generatingType === 'nachtrag'
                          ? '⏳ Vorschau...'
                          : 'Nachtragsrechnung'}
                      </button>
                    )}
                    {/* T-165 (v1.8.46): Nachmeldung vor der Schlussrechnung abrechnen (AGB: Nachmeldungen sofort zu 100 % fällig) */}
                    {!hasFinal && (
                      <button
                        className="btn-primary btn-sm"
                        onClick={() => setNachmeldungBuchung({ id: booking.id, bookingNumber: booking.bookingNumber })}
                        disabled={generatingBookingId !== null}
                        title="Nachtragsrechnung für nachgemeldete Personen oder Leistungen vor der Schlussrechnung (Mengen wählen, Vorschau)"
                      >
                        Nachmeldung abrechnen
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {filteredBookings.length === 0 && bookings.length > 0 && (
            <div className="empty-state">
              <p>Keine Buchungen gefunden mit den aktuellen Filtern</p>
            </div>
          )}

          {bookings.length === 0 && (
            <div className="empty-state">
              <p>Keine Buchungen für Rechnungserstellung verfügbar</p>
            </div>
          )}
        </div>
      )}

      {(view === 'detail' || view === 'generate') && selectedInvoice && (
        <div className="invoice-detail">
          <div className="detail-header">
            <button
              className="btn-back"
              onClick={() => {
                if (view === 'generate') {
                  // Just close detail, stay in generate view
                  setSelectedInvoice(null);
                } else {
                  setView('list');
                  setSelectedInvoice(null);
                }
              }}
            >
              {view === 'generate' ? '✕ Schließen' : '← Zurück zur Liste'}
            </button>
            <h2>Rechnung {selectedInvoice.invoiceNumber}</h2>
          </div>

          <div className="detail-grid">
            {/* Rechnungsinfo */}
            <div className="detail-section">
              <h3>Rechnungsinformationen</h3>
              <div className="info-row">
                <span className="info-label">Typ:</span>
                <span className="info-value">{getTypeLong(selectedInvoice.typeDisplayName)}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Kunde:</span>
                <span className="info-value">{selectedInvoice.billingName || selectedInvoice.BillingName || '-'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Datum:</span>
                <span className="info-value">{new Date(selectedInvoice.invoiceDate).toLocaleDateString('de-DE')}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Fällig:</span>
                <span className="info-value">{selectedInvoice.dueDate ? new Date(selectedInvoice.dueDate).toLocaleDateString('de-DE') : '-'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Betrag:</span>
                <span className="info-value">{selectedInvoice.totalAmount.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span>
              </div>
              <div className="info-row">
                <span className="info-label">Offen:</span>
                <span className="info-value">{selectedInvoice.amountDue.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span>
              </div>
              <div className="info-row">
                <span className="info-label">Status:</span>
                <span className={`status-badge ${getStatusBadge(selectedInvoice.status)}`}>
                  {getStatusLabel(selectedInvoice.status)}
                </span>
              </div>
            </div>

            {/* Referenzfelder - EDITABLE */}
            <div className="detail-section reference-section">
              <h3>Referenzfelder (für Migration)</h3>
              <p className="section-hint">Hier können alte Rechnungsnummern und interne Notizen hinterlegt werden.</p>

              <div className="form-group">
                <label htmlFor="customerRef">Kundenreferenz / Alte Rechnungsnr.</label>
                <input
                  id="customerRef"
                  type="text"
                  value={editCustomerRef}
                  onChange={(e) => setEditCustomerRef(e.target.value)}
                  placeholder="z.B. alte Rechnungsnummer aus Altsystem"
                  className="form-input"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="adminNotes">Interne Notizen (nur Admin sichtbar)</label>
                <textarea
                  id="adminNotes"
                  value={editAdminNotes}
                  onChange={(e) => setEditAdminNotes(e.target.value)}
                  placeholder="Interne Notizen zur Rechnung..."
                  className="form-textarea"
                  rows={4}
                  disabled={saving}
                />
              </div>

              <button
                className="btn-primary"
                onClick={saveInvoiceReferences}
                disabled={saving}
              >
                {saving ? 'Speichern...' : 'Referenzfelder speichern'}
              </button>
            </div>
          </div>

          {/* Aktionen */}
          <div className="detail-actions">
            <button
              className="btn-secondary"
              onClick={() => downloadPdf(selectedInvoice.id, selectedInvoice.invoiceNumber)}
            >
              📄 PDF herunterladen
            </button>
            {selectedInvoice.status === 'Cancelled' && selectedInvoice.type !== 'Angebot' && (
              <button
                className="btn-secondary"
                onClick={() => downloadStornorechnung(selectedInvoice.id, selectedInvoice.invoiceNumber)}
                title="Stornorechnung (Nummernkreis ST) zu dieser stornierten Rechnung"
              >
                📄 Stornorechnung (PDF)
              </button>
            )}
            <button
              className={`btn-secondary ${sendingEmailInvoiceId === selectedInvoice.id ? 'btn-loading' : ''}`}
              onClick={() => sendInvoiceEmail(selectedInvoice.id, selectedInvoice.invoiceNumber)}
              disabled={sendingEmailInvoiceId !== null}
            >
              {sendingEmailInvoiceId === selectedInvoice.id ? '⏳ Wird gesendet...' : '📧 Per E-Mail senden'}
            </button>
          </div>
          {/* E8/T-064 (v1.8.49): Zahlung ohne Bankabgleich erfassen (bar oder Überweisung) */}
          <ZahlungErfassenBereich
            key={`ze-${selectedInvoice.id}`}
            rechnung={selectedInvoice}
            onVerbucht={(hinweis) => {
              setEmailSuccessMessage(hinweis);
              setSelectedInvoice(null);
              if (view === 'detail') setView('list');
              loadInvoices();
              loadBookings();
            }}
          />
          {/* T-165 (v1.8.46): Rechnung ohne Zahlungseingang stornieren */}
          <RechnungStornoBereich
            key={selectedInvoice.id}
            rechnung={selectedInvoice}
            onStorniert={(hinweis) => {
              setEmailSuccessMessage(hinweis);
              setSelectedInvoice(null);
              loadInvoices();
              loadBookings();
            }}
          />
        </div>
      )}

      {nachmeldungBuchung && (
        <NachmeldungDialog
          bookingId={nachmeldungBuchung.id}
          bookingNumber={nachmeldungBuchung.bookingNumber}
          onClose={() => setNachmeldungBuchung(null)}
          onErstellt={(hinweis) => {
            setNachmeldungBuchung(null);
            setEmailSuccessMessage(hinweis);
            loadInvoices();
            loadBookings();
            setView('list');
          }}
        />
      )}
    </div>
  );
};

export default InvoiceManagement;
